import { fetchAuthSession, signOut } from 'aws-amplify/auth';

export type OrgInviteLandingPage = 'org-referral' | 'org-admin' | 'register' | 'parent-referral';

const INVITE_STORAGE_KEYS = [
  'pendingInvitationToken',
  'pendingInvitationPage',
  'pendingVerification',
  'redirectAfterLogin',
] as const;

/** Drop stale invite context so a prior session cannot override the current link. */
export function resetInviteLandingStorage(): void {
  for (const key of INVITE_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

/** Persist the token from the current URL before OAuth or registration. */
export function storeInviteLandingContext(token: string, page: OrgInviteLandingPage): void {
  resetInviteLandingStorage();
  localStorage.setItem('pendingInvitationToken', token);
  localStorage.setItem('pendingInvitationPage', page);
}

export function inviteEmailFromRecord(email: string | null | undefined): string {
  return String(email || '').toLowerCase().trim();
}

/**
 * If another Cognito user is already signed in, sign out so invite email/password
 * fields are not polluted by the previous account (browser + Amplify session).
 */
export async function clearConflictingAuthSessionForInvite(invitedEmail: string): Promise<void> {
  const normalizedInvite = inviteEmailFromRecord(invitedEmail);
  if (!normalizedInvite) return;

  try {
    const session = await fetchAuthSession();
    const currentEmail = inviteEmailFromRecord(session.tokens?.idToken?.payload?.email as string | undefined);
    if (currentEmail && currentEmail !== normalizedInvite) {
      await signOut();
    }
  } catch {
    // Not signed in — nothing to clear.
  }
}

/** Props that reduce browser password-manager autofill on invite registration forms. */
export const inviteRegistrationAutofillProps = {
  autoComplete: 'off',
  'data-lpignore': 'true',
  'data-form-type': 'other',
} as const;
